import {filter, merge} from 'rxjs';
import {useEffect} from 'react';

import {Conversation} from '@stripe-internal/conversations-client';

import {useApp, useSetAppError} from './appHooks';

export function useConversationExceptions(conversation?: Conversation | null) {
  const app = useApp();
  const setAppError = useSetAppError();

  useEffect(() => {
    if (!conversation) {
      return;
    }

    const {client} = conversation;

    const clientExceptions$ = client
      .eventsObservable()
      .pipe(filter((e) => e.name === 'client-exception'));
    const clientConversationExceptions$ = client
      .conversationEventsObservable()
      .pipe(filter((e) => e.name === 'conversation-exception'));
    const conversationExceptions$ = conversation
      .eventsObservable()
      .pipe(filter((e) => e.name === 'conversation-exception'));

    const sub = merge(
      clientExceptions$,
      clientConversationExceptions$,
      conversationExceptions$,
    ).subscribe((e) => {
      const cleanupAndShowError = () => {
        conversation.clearStorage();
        setAppError({
          errorId: e.sentryEventId,
        });
        app.endConversation(conversation);
      };

      if (conversation.client.channel === 'chat') {
        try {
          // attempt to send the agent a message telling them the user
          // has encountered an error and that their conversation
          // has ended before we clear the conversation and transition
          conversation
            .sendMessage(
              conversation.buildTextMessage(
                'Error: The user encountered a client error and has left the conversation.',
              ),
            )
            .then(cleanupAndShowError);
        } catch {
          cleanupAndShowError();
        }
      } else {
        cleanupAndShowError();
      }
    });

    return () => sub.unsubscribe();
  }, [app, conversation, setAppError]);
}
